import React, { useCallback, useEffect, useState } from 'react';
import { stringify } from 'query-string';
import { baseConfig } from '../../config/algolia';
import { getLocal, setLocal } from '@jbhi-fi/utils/src/localStorage';
import { InstantSearch, Configure } from 'react-instantsearch';
import GtmContext from '../../contexts/GtmContext';
import SearchBar from './SearchBar';
import { SessionStorageKeys, setSession } from '@jbhi-fi/utils/src/sessionStorage';
import { pushToDataLayer } from '@jbhi-fi/utils/src/ga4Tracking/datalayer';
import { getAnalyticsTags } from '../../utils/algolia/search-insights';
import { InsightsMiddleware } from '../../utils/algolia/InsightsMiddleware';
import { createSearchClient } from '../../config/searchClient';

const { search_api_key, product_families_index } = baseConfig;
const legacySearchInputQuery = '#quicksearch-search-box input';

const searchClientInstance = createSearchClient(search_api_key);
const indexName = product_families_index;

/**
 * Search container component for search layover
 */
const SearchContainer = () => {
  const legacySearchInputElement = document.querySelector(legacySearchInputQuery);
  const [searchTerm, setSearchTerm] = useState(legacySearchInputElement?.value ?? '');
  const [showContainer, setShowContainer] = useState(legacySearchInputElement === document.activeElement);
  const [showQuickResults, setShowQuickResults] = useState(false);
  const [selectionRange, setSelectionRange] = useState({ start: 0, end: -1 });
  const [gtm, setGtm] = useState({
    group: 'suggested search',
    category: 'search',
    pageType: 'Search Pages',
  });
  const [rangeValues, setRangeValues] = useState({});

  useEffect(() => {
    if (showContainer) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
  }, [showContainer]);

  /**
   * Add event listener to og search input box
   */
  const handleSearchInputClick = e => {
    e.preventDefault();
    if (e.target.selectionStart !== e.target.selectionEnd) {
      return;
    }
    e.target.blur();
    setSelectionRange({ start: 0, end: -1 });
    setShowLayover(true);
  };

  const handleSearchInputSelect = e => {
    e.preventDefault();
    e.target.blur();
    highlightSelection(e.target);
    setShowLayover(true);
  };

  const highlightSelection = target => {
    const startHighlightPosition = target.selectionStart;
    const endHighlightPosition = target.selectionEnd;

    setSelectionRange({
      start: startHighlightPosition,
      end: endHighlightPosition,
    });
  };

  const handleSearchInputKeyboardNavigation = e => {
    if (e.key === 'Enter') {
      window.location.href = `/search?query=${e.target.value}`;
    } else if (e.key === 'Tab') {
      e.target.blur();
      setSelectionRange({ start: 0, end: -1 });
      setShowLayover(true);
    }
  };

  useEffect(() => {
    const searchBox = document.querySelector(legacySearchInputQuery);

    searchBox.addEventListener('select', handleSearchInputSelect);
    searchBox.addEventListener('click', handleSearchInputClick);
    searchBox.addEventListener('keydown', handleSearchInputKeyboardNavigation);

    return () => {
      searchBox.removeEventListener('select', handleSearchInputSelect);
      searchBox.removeEventListener('click', handleSearchInputClick);
      searchBox.removeEventListener('keydown', handleSearchInputKeyboardNavigation);
    };
  }, []);

  useEffect(() => {
    setSelectionRange({ start: 0, end: -1 });
  }, [searchTerm]);

  const escFunction = useCallback(
    event => {
      if (!showContainer || event.key !== 'Escape') return;
      event.preventDefault();

      closeSearch();

      // Focus on next focusable element when closed
      const searchBox = document.querySelector(legacySearchInputQuery);
      if (!searchBox) return;

      const header = document.querySelector('header');
      const focusableElements = [
        ...header.querySelectorAll('a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'),
      ];
      const nextElement = focusableElements[focusableElements.indexOf(searchBox) + 1];
      if (nextElement) {
        nextElement.focus();
      }
    },
    // eslint-disable-next-line
    [showContainer]
  );

  useEffect(() => {
    document.addEventListener('keyup', escFunction);

    return () => {
      document.removeEventListener('keyup', escFunction, false);
    };
  }, [escFunction]);

  /**
   * Function to determine when to show the search layover container and append event listeners
   */
  const setShowLayover = value => {
    setShowContainer(value);

    if (value === true) {
      document.body.classList.add('search-is-active');
      document.body.addEventListener('click', externalClick);
    } else {
      document.body.classList.remove('search-is-active');
      document.body.removeEventListener('click', externalClick);
      setShowQuickResults(false);
    }
  };

  /**
   * Function to close the search layover and clear all variables
   */
  const closeSearch = () => {
    setShowLayover(false);
    setSearchTerm('');
  };

  /**
   * Function to handle external clicks (clicks not within the search layover container)
   */
  const externalClick = event => {
    const searchLayover = document.querySelector('#search-layover-container .search-container');
    const searchBox = document.querySelector('#quicksearch-search-box');
    const tooltip = event.target.closest('.react-tooltip');

    // Close search container if clicked outside of target window (desktop)
    if (
      searchLayover &&
      (!event ||
        (!searchBox.contains(event.target) &&
          !searchLayover.contains(event.target) &&
          !tooltip &&
          document.body.classList.contains('search-is-active')))
    ) {
      closeSearch();
    }
  };

  /**
   * Search function when search form submitted
   */
  const onSearch = (term, method, priceRange, saleItems) => {
    const { max: max_range, min: min_range } = rangeValues;
    const { max, min } = priceRange;
    let price = {};
    if (max && max_range !== max) {
      price['Max Price'] = max;
    }
    if (min && min_range !== min) {
      price['Min Price'] = min;
    }

    if (saleItems) {
      price['Sale items'] = saleItems;
    }
    const filters = { price };
    pushToDataLayer({
      event: 'search',
      search_term: term,
      search_type: 'suggested search',
      method,
      filters,
    });
    setSession(SessionStorageKeys.SearchMethod, method);

    const storedSearches = JSON.parse(getLocal('recentSearches')) || [];

    if (storedSearches.includes(term)) {
      setLocal('recentSearches', JSON.stringify([term, ...storedSearches.filter(item => item !== term)]));
    } else {
      if (storedSearches.length >= 10) {
        storedSearches.pop();
      }
      setLocal('recentSearches', JSON.stringify([term, ...storedSearches]));
    }

    // We redirect to search page because injecting search results on cart page breaks the mini cart
    const hasPriceFilter = (min && min_range !== min) || (max && max_range !== max);
    const searchUrlQuery = stringify({
      query: term,
      'Price[min]': hasPriceFilter ? min : undefined,
      'Price[max]': hasPriceFilter ? max : undefined,
      saleItems: !!saleItems || undefined,
    });

    window.location = `/search?${searchUrlQuery}`;
  };

  if (!showContainer) {
    return <></>;
  }

  return (
    <GtmContext.Provider value={{ gtm, setGtm }}>
      <InstantSearch
        searchClient={searchClientInstance}
        indexName={indexName}
        future={{
          persistHierarchicalRootCount: false,
          preserveSharedStateOnUnmount: true,
        }}
      >
        <InsightsMiddleware />
        <Configure clickAnalytics analyticsTags={getAnalyticsTags('search')} />
        <div className="search-container">
          <div className="page-width">
            <SearchBar
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm.bind(this)}
              onSearch={onSearch.bind(this)}
              showQuickResults={showQuickResults}
              setShowQuickResults={setShowQuickResults.bind(this)}
              closeSearch={closeSearch.bind(this)}
              selectionRange={selectionRange}
              setRangeValues={setRangeValues}
            />
          </div>
        </div>
        <div className="search-underlay"></div>
      </InstantSearch>
    </GtmContext.Provider>
  );
};
export default SearchContainer;
